import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ---------------------------------------------------------------------------
// Amenity image cards
//
// Each card is its own slot group so the editor edits image + alt + title as a
// coherent unit, and each carries a distinct `generator` so the backend can
// pre-select a real featured amenity (and its matching photo) per card without
// repeating a pick. `sourceBinding` is the editor's swap source; generation
// picks its own item. Literals are repeated per card (no factory) so the schema
// stays statically extractable and the generator prompts can differ.
// ---------------------------------------------------------------------------

const CARD_IMAGE_SIZES = '(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw';

const card1Def: SlotGroupDefinition = {
  label: 'Amenity Card 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[0].amenity_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'amenities[0].amenity_image_alt_text' },
    title: { label: 'Title', type: 'text', defaultField: 'amenities[0].title' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 0,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt: 'A featured amenity that best represents this property, paired with its photo.',
    context:
      'Card 1 of 8 in the featured amenities grid. Pick the single strongest amenity here. Do not reuse the amenity or photo chosen for cards 2-8.',
  },
};

const card2Def: SlotGroupDefinition = {
  label: 'Amenity Card 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[1].amenity_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'amenities[1].amenity_image_alt_text' },
    title: { label: 'Title', type: 'text', defaultField: 'amenities[1].title' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 1,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt: 'Another standout amenity with a strong photo of its own.',
    context:
      'Card 2 of 8 in the featured amenities grid. A different amenity and photo from cards 1 and 3-8.',
  },
};

const card3Def: SlotGroupDefinition = {
  label: 'Amenity Card 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[2].amenity_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'amenities[2].amenity_image_alt_text' },
    title: { label: 'Title', type: 'text', defaultField: 'amenities[2].title' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 2,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt: 'A distinct amenity worth featuring, with a photo that suits it.',
    context:
      'Card 3 of 8 in the featured amenities grid. Not the same as cards 1, 2, or 4-8.',
  },
};

const card4Def: SlotGroupDefinition = {
  label: 'Amenity Card 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[3].amenity_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'amenities[3].amenity_image_alt_text' },
    title: { label: 'Title', type: 'text', defaultField: 'amenities[3].title' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 3,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt: 'A further amenity highlight with its own representative image.',
    context:
      'Card 4 of 8 in the featured amenities grid. Different from cards 1-3 and 5-8.',
  },
};

const card5Def: SlotGroupDefinition = {
  label: 'Amenity Card 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[4].amenity_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'amenities[4].amenity_image_alt_text' },
    title: { label: 'Title', type: 'text', defaultField: 'amenities[4].title' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 4,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt: 'An additional amenity that reads well as a photo card. Leave empty if there is nothing distinct left to feature.',
    context:
      'Card 5 of 8 in the featured amenities grid. Different from cards 1-4 and 6-8. Optional.',
  },
};

const card6Def: SlotGroupDefinition = {
  label: 'Amenity Card 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[5].amenity_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'amenities[5].amenity_image_alt_text' },
    title: { label: 'Title', type: 'text', defaultField: 'amenities[5].title' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 5,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt: 'Another photo-worthy amenity to feature. Leave empty if there is nothing distinct left.',
    context:
      'Card 6 of 8 in the featured amenities grid. Different from cards 1-5 and 7-8. Optional.',
  },
};

const card7Def: SlotGroupDefinition = {
  label: 'Amenity Card 7',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[6].amenity_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'amenities[6].amenity_image_alt_text' },
    title: { label: 'Title', type: 'text', defaultField: 'amenities[6].title' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 6,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt: 'A further distinct amenity with its own image. Leave empty if there is nothing left to feature.',
    context:
      'Card 7 of 8 in the featured amenities grid. Different from cards 1-6 and 8. Optional.',
  },
};

const card8Def: SlotGroupDefinition = {
  label: 'Amenity Card 8',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[7].amenity_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'amenities[7].amenity_image_alt_text' },
    title: { label: 'Title', type: 'text', defaultField: 'amenities[7].title' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 7,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt: 'A final amenity to round out the grid. Leave empty if there is nothing distinct left to feature.',
    context:
      'Card 8 of 8 in the featured amenities grid. Different from cards 1-7. Optional.',
  },
};

interface CardData {
  image?: string | null;
  alt?: string | null;
  title?: string | null;
}

function AmenityCardContent({ data }: { data: CardData }) {
  const title = (data.title || '').trim();
  return (
    <div className="group relative aspect-[4/3] overflow-hidden">
      <VibesitesImage
        url={data.image || ''}
        alt={data.alt || title || 'Featured amenity'}
        sizes={CARD_IMAGE_SIZES}
        objectFit="cover"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-black/45 transition-colors duration-300 group-hover:bg-black/55" />
      {title && (
        <div className="absolute inset-0 flex items-center justify-center px-3 text-center">
          <span className="font-heading text-sm font-bold leading-snug text-white drop-shadow-md sm:text-base">
            {title}
          </span>
        </div>
      )}
    </div>
  );
}

function CardPlaceholder() {
  return (
    <div className="flex aspect-[4/3] items-center justify-center border-2 border-dashed border-black/15 bg-black/[0.03] px-3 text-center">
      <span className="font-body text-xs text-[var(--vibe-color-text-muted)]">
        Featured amenity — generate or pick a photo
      </span>
    </div>
  );
}

export function FeaturedAmenities() {
  const isEditMode = !!useEditMode()?.isEditMode;

  const card1 = useSlotGroup('featuredamenities.card1', card1Def);
  const card2 = useSlotGroup('featuredamenities.card2', card2Def);
  const card3 = useSlotGroup('featuredamenities.card3', card3Def);
  const card4 = useSlotGroup('featuredamenities.card4', card4Def);
  const card5 = useSlotGroup('featuredamenities.card5', card5Def);
  const card6 = useSlotGroup('featuredamenities.card6', card6Def);
  const card7 = useSlotGroup('featuredamenities.card7', card7Def);
  const card8 = useSlotGroup('featuredamenities.card8', card8Def);

  const hasAnyCard =
    !!(card1.image || card2.image || card3.image || card4.image ||
       card5.image || card6.image || card7.image || card8.image);

  // On a deployed site with no generated/bound photos, collapse the whole
  // section rather than render an empty band. In edit mode we always show the
  // grid (with placeholders) so operators have a click target.
  if (!hasAnyCard && !isEditMode) return null;

  return (
    <section className="bg-background py-12 md:py-16">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="featuredamenities.heading"
          defaultValue="<p>Featured Amenities</p>"
          as="h2"
          className="mb-8 text-center font-heading text-2xl font-bold uppercase tracking-[0.15em] text-[var(--vibe-color-text)] sm:text-3xl md:mb-10"
        />

        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
          {(card1.image || isEditMode) && (
            <EditableSlotGroup groupPath="featuredamenities.card1" definition={card1Def}>
              {card1.image ? <AmenityCardContent data={card1} /> : <CardPlaceholder />}
            </EditableSlotGroup>
          )}
          {(card2.image || isEditMode) && (
            <EditableSlotGroup groupPath="featuredamenities.card2" definition={card2Def}>
              {card2.image ? <AmenityCardContent data={card2} /> : <CardPlaceholder />}
            </EditableSlotGroup>
          )}
          {(card3.image || isEditMode) && (
            <EditableSlotGroup groupPath="featuredamenities.card3" definition={card3Def}>
              {card3.image ? <AmenityCardContent data={card3} /> : <CardPlaceholder />}
            </EditableSlotGroup>
          )}
          {(card4.image || isEditMode) && (
            <EditableSlotGroup groupPath="featuredamenities.card4" definition={card4Def}>
              {card4.image ? <AmenityCardContent data={card4} /> : <CardPlaceholder />}
            </EditableSlotGroup>
          )}
          {(card5.image || isEditMode) && (
            <EditableSlotGroup groupPath="featuredamenities.card5" definition={card5Def}>
              {card5.image ? <AmenityCardContent data={card5} /> : <CardPlaceholder />}
            </EditableSlotGroup>
          )}
          {(card6.image || isEditMode) && (
            <EditableSlotGroup groupPath="featuredamenities.card6" definition={card6Def}>
              {card6.image ? <AmenityCardContent data={card6} /> : <CardPlaceholder />}
            </EditableSlotGroup>
          )}
          {(card7.image || isEditMode) && (
            <EditableSlotGroup groupPath="featuredamenities.card7" definition={card7Def}>
              {card7.image ? <AmenityCardContent data={card7} /> : <CardPlaceholder />}
            </EditableSlotGroup>
          )}
          {(card8.image || isEditMode) && (
            <EditableSlotGroup groupPath="featuredamenities.card8" definition={card8Def}>
              {card8.image ? <AmenityCardContent data={card8} /> : <CardPlaceholder />}
            </EditableSlotGroup>
          )}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeaturedAmenities',
  displayName: 'Featured Amenities',
  tokens: {
    'featuredamenities.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Featured Amenities</p>',
    },
  },
  slotGroups: {
    'featuredamenities.card1': card1Def,
    'featuredamenities.card2': card2Def,
    'featuredamenities.card3': card3Def,
    'featuredamenities.card4': card4Def,
    'featuredamenities.card5': card5Def,
    'featuredamenities.card6': card6Def,
    'featuredamenities.card7': card7Def,
    'featuredamenities.card8': card8Def,
  },
};

(FeaturedAmenities as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeaturedAmenities;
